import type { CacheValue } from './type';

import { useRef, useEffect, useCallback } from 'react';

// ---------------------------------------------------------------------------
// useFallbackTimer
// Watches cache entries: for any requestId with no data and no pending timer,
// fires onFallback(requestId) after `timeout` ms as a WS fallback.
// ---------------------------------------------------------------------------
export function useFallbackTimer<T>(
  cache: Record<string, CacheValue<T>>,
  onFallback: (requestId: string) => void,
  timeout: number,
) {
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    if (!timeout) return;

    Object.values(cache).forEach(({ requestId, data }) => {
      if (data != null || !requestId || timers.current.has(requestId)) return;

      const timer = setTimeout(() => {
        timers.current.delete(requestId);
        onFallback(requestId);
      }, timeout);

      timers.current.set(requestId, timer);
    });
  }, [cache, timeout, onFallback]);

  const cancel = useCallback((requestId: string) => {
    const timer = timers.current.get(requestId);
    if (!timer) return;

    clearTimeout(timer);
    timers.current.delete(requestId);
  }, []);

  useEffect(() => {
    const timersOnMount = timers.current;
    return () => {
      timersOnMount.forEach(clearTimeout);
      timersOnMount.clear();
    };
  }, []);

  return { cancel };
}
